import { findFencedBlock } from './factory-questions';

/**
 * SF-T-308: what the AI builder did with the suggestions people sent to the Backlog for it to make. Its report ends
 * with one fenced block,
 *
 * ```factory-backlog
 * { "items": [{ "run": "R-27", "number": 2, "title": "Add tests", "task": "SF-T-123" },
 *             { "run": "R-27", "number": 3, "title": "Rename", "reason": "SF-T-90 already covers it" }] }
 * ```
 *
 * one item per Backlog pick: the task it made, or why it made none. A question before the build marked Backlog is
 * reported the same way, with `question` (its number in that round) in place of `number`: its line in the spec's
 * Decided section and its answer on the task say what came of it. The task's suggestion rounds read it onto those
 * decisions (the card links the task, or shows the reason), and a suggestions-first run doesn't wrap itself up
 * while a Backlog pick it was handed has neither.
 */

export const FACTORY_BACKLOG_LANG = 'factory-backlog';

export interface FactoryBacklogItem {
  /** A suggestion (a wrap round's item), or a question asked before the build. */
  kind: 'suggestion' | 'question';
  /** The run whose suggestions the pick was on (from the decisions' header), when the agent named it. */
  run: number | null;
  /** The suggestion's number in that round (1-based). */
  number: number;
  /** Its title, as the decisions named it, to tell rounds apart. */
  title?: string;
  /** The task made ("SF-T-123", or "T-123"). */
  task?: string;
  /** Why no task was made (the agent's call). */
  reason?: string;
}

const text = (v: unknown, max: number) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const TASK_RE = /^(?:[A-Z]{2,}-)?T-\d+$/;

/** The block's JSON, checked: items with a number and a task ref or a reason. Null when none is usable. */
export function parseFactoryBacklogJson(raw: string): FactoryBacklogItem[] | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const obj = (Array.isArray(data) ? { items: data } : data) as Record<string, unknown> | null;
  const list = obj && typeof obj === 'object' ? [obj.items, obj.backlog].find(Array.isArray) : undefined;
  if (!list) return null;
  const items: FactoryBacklogItem[] = [];
  for (const raw of list.slice(0, 20)) {
    const item = (raw ?? {}) as Record<string, unknown>;
    // Naming a question makes it a question's, whatever else it carries.
    const kind = item.question !== undefined && item.question !== null ? 'question' : 'suggestion';
    const number = Number(kind === 'question' ? item.question : item.number);
    if (!Number.isInteger(number) || number < 1) continue;
    const task = text(item.task, 40).toUpperCase();
    const reason = text(item.reason, 1000);
    if (!TASK_RE.test(task) && !reason) continue;
    const run = /^R?-?(\d+)$/i.exec(text(item.run, 20))?.[1];
    const title = text(item.title, 200);
    items.push({
      kind,
      run: run ? Number(run) : null,
      number,
      ...(title ? { title } : {}),
      ...(TASK_RE.test(task) ? { task } : { reason }),
    });
  }
  return items.length ? items : null;
}

/** The message's factory-backlog block, read; null when there's none (or it's broken). */
export function findFactoryBacklog(message: string): FactoryBacklogItem[] | null {
  const block = findFencedBlock(message, FACTORY_BACKLOG_LANG);
  return block ? parseFactoryBacklogJson(block.body) : null;
}

/** A title as compared: letters and digits only, lower case ("Add tests." is "add tests"). */
const plain = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/**
 * The round a report's item is about, among the rounds (of its run, when it names one) whose pick at that number is a
 * Backlog with nothing reported yet (`awaiting`): the newest whose title matches the item's (letters and digits
 * compared); else, when exactly one is open there, that one (a title written differently, or none given). With
 * several open and none matching, it settles nothing rather than guess at the wrong pick.
 */
export function backlogItemRound<R>(
  rounds: R[],
  item: FactoryBacklogItem,
  runOf: (round: R) => number | null,
  awaiting: (round: R, number: number) => string | null
): R | undefined {
  const open = rounds.filter((r) => (item.run === null || runOf(r) === item.run) && awaiting(r, item.number) !== null).reverse();
  const titled = item.title ? open.find((r) => plain(awaiting(r, item.number)!) === plain(item.title!)) : undefined;
  return titled ?? (open.length === 1 ? open[0] : undefined);
}

/** SF-T-308: the line in a Backlog decision's place once the agent reported it: the task, or why none. */
export type BacklogOutcome = { task: string } | { reason: string };
